import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Divider,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Stack,
  alpha,
  useTheme,
} from "@mui/material";
import {
  ThumbUp,
  ThumbsUpDown,
  ThumbDown,
  Medication,
  CheckCircle,
  Cancel,
  AccessTime,
} from "@mui/icons-material";
import moment from "moment";
import { NOT_GIVEN_REASONS } from "./medicines.config";
import CustomInput from "../../../components/CustomForm/CustomInput";
import CustomSelect from "../../../components/FormFields/CustomSelect/CustomSelect";

const stockIcons = {
  "In Stock": <ThumbUp />,
  "Low Stock": <ThumbsUpDown />,
  "Out of Stock": <ThumbDown />,
};

const getStockColor = (status, theme) => {
  switch (status) {
    case "In Stock":
      return theme.palette.success.main;
    case "Low Stock":
      return theme.palette.warning.main;
    case "Out of Stock":
      return theme.palette.error.main;
    default:
      return theme.palette.text.primary;
  }
};

const MedicationSection = ({ medicines, handleMedicinesChange }) => {
  const theme = useTheme();
  const [medState, setMedState] = useState([]);

  useEffect(() => {
    const initialized = medicines.map((med) => ({
      ...med,
      localStock: med.stock_status || "",
      localStatus: med.status || "",
      localNote: med.notes || "",
      localReason: med.reason || "",
    }));
    setMedState(initialized);
  }, [medicines]);

  const updateMedicine = (id, field, value) => {
    const updated = medState.map((med) =>
      med.id === id ? { ...med, [field]: value } : med
    );
    setMedState(updated);
    handleMedicinesChange(
      updated.map(
        ({ id, localStock, localStatus, localNote, localReason }) => ({
          id,
          stock_status: localStock,
          status: localStatus,
          notes: localNote,
          reason: localReason,
        })
      )
    );
  };

  return (
    <Card
      variant="outlined"
      sx={{
        p: 2.5,
        borderRadius: 2,
        borderColor: alpha(theme.palette.divider, 0.1),
        bgcolor: alpha(theme.palette.background.default, 0.3),
      }}
    >
      {medState.map((med, index) => (
        <Box key={med.id}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
            <Medication
              sx={{
                color: theme.palette.primary.main,
                fontSize: 22,
              }}
            />
            <Chip
              label={med.title}
              size="small"
              sx={{
                fontWeight: 600,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.primary.main,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
              }}
            />
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <AccessTime sx={{ fontSize: 16, color: "text.secondary" }} />
              <Typography variant="body2" fontWeight={600} color="text.secondary">
                {moment(med.time, "HH:mm:ss").format("HH:mm")}
              </Typography>
            </Stack>
          </Stack>

          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            {med.medicine_name} ({med.medicine_type})
            {med.dose && (
              <Typography component="span" variant="body2" fontWeight={500} color="text.secondary" sx={{ ml: 1 }}>
                - {med.dose} dose
              </Typography>
            )}
          </Typography>

          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <Typography variant="body2" fontWeight={500} color="text.secondary">
              Stock:
            </Typography>
            {["In Stock", "Low Stock", "Out of Stock"].map((status) => {
              const color = getStockColor(status, theme);
              return (
                <IconButton
                  key={status}
                  onClick={() => updateMedicine(med.id, "localStock", status)}
                  size="small"
                  sx={{
                    color: med.localStock === status ? color : theme.palette.text.disabled,
                    bgcolor: med.localStock === status ? alpha(color, 0.08) : "transparent",
                    borderRadius: 2,
                    p: 0.75,
                    "&:hover": {
                      bgcolor: med.localStock === status ? alpha(color, 0.12) : alpha(theme.palette.text.disabled, 0.04),
                    },
                  }}
                >
                  {stockIcons[status]}
                </IconButton>
              );
            })}
          </Stack>

          <CustomInput
            placeholder="Enter any medication notes"
            value={med.localNote}
            onChange={(e) => updateMedicine(med.id, "localNote", e.target.value)}
            sx={{
              mb: 1.5,
              "& .MuiInputBase-input": {
                fontSize: "0.85rem",
                padding: "8px 12px",
              },
            }}
          />

          <Stack direction="row" alignItems="center" spacing={2}>
            <ToggleButtonGroup
              value={med.localStatus}
              exclusive
              onChange={(e, newVal) =>
                newVal && updateMedicine(med.id, "localStatus", newVal)
              }
              size="small"
              sx={{
                "& .MuiToggleButton-root": {
                  borderRadius: 2,
                  px: 2,
                  py: 0.75,
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  textTransform: "none",
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  "&.Mui-selected": {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                  },
                },
              }}
            >
              <ToggleButton value="Not Given">
                <Cancel sx={{ mr: 0.5, fontSize: 16 }} />
                Not Given
              </ToggleButton>
              <ToggleButton value="Given">
                <CheckCircle sx={{ mr: 0.5, fontSize: 16 }} />
                Given
              </ToggleButton>
            </ToggleButtonGroup>

            {med.localStatus === "Not Given" && (
              <Box sx={{ minWidth: 200 }}>
                <CustomSelect
  value={med.localReason}
  placeholder="Select reason"
  options={NOT_GIVEN_REASONS.map((reason) => ({
    value: reason,
    label: reason,
  }))}
  onChange={(e) => updateMedicine(med.id, "localReason", e.target.value)}
  size="small"
  searchable={true}
  fullWidth
/>
              </Box>
            )}
          </Stack>

          {index < medState.length - 1 && (
            <Divider
              sx={{
                my: 3,
                borderColor: alpha(theme.palette.divider, 0.1),
                borderStyle: "dashed",
              }}
            />
          )}
        </Box>
      ))}
    </Card>
  );
};

export default MedicationSection;